import type { ClientPortalArtifact } from '@domain/models/client-portal-artifact.interface';
import type { LabPortalBranding } from '@domain/models/lab-portal-branding.interface';
import type {
  DeliveryListQuery,
  IClientPortalRepository,
} from '@domain/data-contracts/client-portal-repository.interface';
import type { InvoiceListQuery } from '@domain/data-contracts/invoice-repository.interface';
import type { PatientListQuery } from '@domain/data-contracts/patient-repository.interface';
import type { ClientInvoice } from '@domain/models/client-invoice.interface';
import type { ClientOrder } from '@domain/models/client-order.interface';
import type { ClientTimelineStep } from '@domain/models/client-timeline-step.interface';
import type { DeliveryNote } from '@domain/models/delivery-note.interface';
import type { NewOrderDraft } from '@domain/models/new-order-draft.interface';
import type { OrderListQuery, PagedResult } from '@domain/models/paging.interface';
import type { Patient } from '@domain/models/patient.interface';
import type { PracticeProfile } from '@domain/models/practice-profile.interface';
import type { Scanner } from '@domain/models/scanner.interface';

/**
 * Thin domain service over the client-portal repository. Pages
 * consume this through `useServices()` — they never import from
 * `3.data` directly.
 */
export class ClientPortalService {
  public constructor(private readonly repo: IClientPortalRepository) {}

  public getPortalBranding(): Promise<LabPortalBranding> {
    return this.repo.getPortalBranding();
  }

  public listOrders(query: OrderListQuery): Promise<PagedResult<ClientOrder>> {
    return this.repo.listOrders(query);
  }

  public getOrderById(id: string): Promise<ClientOrder> {
    return this.repo.getOrderById(id);
  }

  public getOrderTimeline(orderId: string): Promise<readonly ClientTimelineStep[]> {
    return this.repo.getOrderTimeline(orderId);
  }

  public listOrderAttachments(orderId: string): Promise<readonly ClientPortalArtifact[]> {
    return this.repo.listOrderAttachments(orderId);
  }

  public getArtifactBlob(contentUrl: string): Promise<Blob> {
    return this.repo.getArtifactBlob(contentUrl);
  }

  public reviewDesign(
    orderId: string,
    designRequestId: string,
    decision: 'Approved' | 'RevisionRequested',
    note?: string,
  ): Promise<void> {
    return this.repo.reviewDesign(orderId, designRequestId, decision, note);
  }

  public listInvoices(query: InvoiceListQuery): Promise<PagedResult<ClientInvoice>> {
    return this.repo.listInvoices(query);
  }

  public getInvoicePdfBlob(id: string): Promise<Blob> {
    return this.repo.getInvoicePdfBlob(id);
  }

  public listDeliveries(query: DeliveryListQuery): Promise<PagedResult<DeliveryNote>> {
    return this.repo.listDeliveries(query);
  }

  public getDeliveryPdfBlob(id: string): Promise<Blob> {
    return this.repo.getDeliveryPdfBlob(id);
  }

  public listScanners(): Promise<readonly Scanner[]> {
    return this.repo.listScanners();
  }

  public getPracticeProfile(): Promise<PracticeProfile> {
    return this.repo.getPracticeProfile();
  }

  public listPatients(query: PatientListQuery): Promise<PagedResult<Patient>> {
    return this.repo.listPatients(query);
  }

  public submitOrder(draft: NewOrderDraft): Promise<ClientOrder> {
    return this.repo.submitOrder(draft);
  }
}
